import fetcher from 'api/fetcher';
import { convertRublesToCents } from 'utils/clickme';
import { MagritteNotificationName } from 'utils/constants/notification';

import dashboardMultiActionsIncreaseBudgetConfirmButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_multi_actions_increase_budget_confirm_button_click';

import { translate } from 'hooks/translation/useTrls';

import { fetchCampaignsCount } from 'components/CampaignCard/CampaignInfo/Actions/fetchCampaignsCount';

import { setCampaigns, setCheckedCampaignIds } from 'store/models/dashboard';
import {
    addMagritteCustomNotification,
    addMagritteNotification,
    MagritteNotificationType,
} from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

export interface IncreaseBudgetResponse {
    updatedCampaignIds: Array<number>;
    failedCampaignIds: Array<number>;
}

const Trls = {
    notificationSuccess: 'dashboard.campaign.actions.multiBudgetIncrease.notification.success',
    campaigns: {
        one: 'Dashboard.MultiActions.budgetIncrease.notification.campaign.one',
        many: 'Dashboard.MultiActions.budgetIncrease.notification.campaign.many',
    },
};

export const batchIncreaseBudget =
    (campaignIds: Record<number, boolean>, budget: string): AppThunkAction =>
    async (dispatch, getState) => {
        const campaigns = getState().dashboard.campaigns;
        const trls = getState().translations.trls;

        const campaignIdsArray = Object.keys(campaignIds).map(Number);

        return fetcher
            .post<unknown, IncreaseBudgetResponse>('/api/v1/campaigns/budget/batch', {
                campaignIds: campaignIdsArray,
                addBudget: Number(budget),
            })
            .then(({ updatedCampaignIds, failedCampaignIds }) => {
                const newCheckedCampaignIds: Record<number, boolean> = {};

                const updatedIds = new Set(updatedCampaignIds);
                const newCampaignsList = campaigns.map((campaign) => {
                    if (updatedIds.has(campaign.id)) {
                        return {
                            ...campaign,
                            budget: Number(campaign.budget || 0) + Number(budget),
                        };
                    }
                    return campaign;
                });

                if (failedCampaignIds.length > 0) {
                    failedCampaignIds.forEach((id) => {
                        newCheckedCampaignIds[id] = true;
                    });
                    dispatch(addMagritteNotification(MagritteNotificationName.CampaignMultiBudgetIncreaseError));
                    dashboardMultiActionsIncreaseBudgetConfirmButtonClick({
                        campaignIds: JSON.stringify(campaignIdsArray),
                        budgetIncreaseValueInCents: Number(convertRublesToCents(budget)),
                        errors: JSON.stringify({
                            failedCampaignIds,
                        }),
                    });
                } else {
                    const campaignTrl = translate(
                        trls,
                        campaignIdsArray.length === 1 ? Trls.campaigns.one : Trls.campaigns.many
                    );
                    dispatch(
                        addMagritteCustomNotification({
                            text: translate(trls, Trls.notificationSuccess, [
                                campaignIdsArray.length,
                                campaignTrl,
                                budget,
                            ]),
                            type: MagritteNotificationType.Success,
                        })
                    );
                    dashboardMultiActionsIncreaseBudgetConfirmButtonClick({
                        campaignIds: JSON.stringify(campaignIdsArray),
                        budgetIncreaseValueInCents: Number(convertRublesToCents(budget)),
                    });
                }

                void dispatch(fetchCampaignsCount());
                dispatch([setCampaigns(newCampaignsList), setCheckedCampaignIds(newCheckedCampaignIds)]);
            })
            .catch((error) => {
                dashboardMultiActionsIncreaseBudgetConfirmButtonClick({
                    campaignIds: JSON.stringify(campaignIdsArray),
                    budgetIncreaseValueInCents: Number(convertRublesToCents(budget)),
                    errors: JSON.stringify(error),
                });
                dispatch(addMagritteNotification(MagritteNotificationName.CampaignMultiBudgetIncreaseError));
            });
    };

export default batchIncreaseBudget;
